/**
 * VerifyBatchModal
 * Shown after upload when unconfirmed phones need paid verification.
 *
 * Flow:
 *   1. User reviews the cost breakdown
 *   2. Per-call clients: toggle "Include reverse phone name check" (optional)
 *   3. Both client types: toggle "Cell phones only" (optional)
 *   4. Click Pay & Verify → calls createPhoneBatchJob → createCheckoutSession → redirect to Stripe
 */

import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Counts {
  good_full: number;
  good_needs_reverse: number;
  bad: number;
  unconfirmed: number;
}

interface LeadRow {
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  sourceFile: string;
}

interface Props {
  counts: Counts;
  leadRows: LeadRow[];
  billingType: 'per_call' | 'per_contact';
  defaultCellOnly?: boolean;
  /**
   * Called when the user clicks "Skip unverified records" — modal closes
   * immediately and the caller is responsible for fire-and-forget queueing
   * via addDeferredVerification (so the user isn't blocked waiting for
   * the Firestore writes to finish).
   */
  onSkip: () => void;
  onSubmitted: () => void;
}

// Pricing constants (mirror of costEstimator.ts)
const UNIT_DISC = 0.004;
const UNIT_CALL_COMBO = 0.025;
const UNIT_CONTACT_COMBO = 0.029;

export function VerifyBatchModal({ counts, leadRows, billingType, defaultCellOnly = false, onSkip, onSubmitted }: Props) {
  const [cellOnly, setCellOnly] = useState(defaultCellOnly);
  // Per-contact clients: always run both (mandatory).
  // Per-call clients: default off, user can opt in.
  const [includeReverse, setIncludeReverse] = useState(billingType === 'per_contact');
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState<'idle' | 'creating' | 'uploading' | 'finalizing' | 'checkout' | 'redirecting'>('idle');
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stageLabel = (() => {
    switch (stage) {
      case 'creating': return 'Creating verification batch...';
      case 'uploading':
        return uploadProgress
          ? `Uploading batch ${uploadProgress.current.toLocaleString()} of ${uploadProgress.total.toLocaleString()}...`
          : 'Uploading batches...';
      case 'finalizing': return 'Finalizing checkout...';
      case 'checkout': return 'Opening secure checkout...';
      case 'redirecting': return 'Redirecting to Stripe...';
      default: return null;
    }
  })();

  // Per-contact clients can't opt out of reverse phone
  const reverseToggleLocked = billingType === 'per_contact';

  const estimate = useMemo(() => {
    const unconfirmed = counts.unconfirmed;
    const goodNeedsReverse = counts.good_needs_reverse;

    if (billingType === 'per_contact') {
      const billed = unconfirmed + goodNeedsReverse;
      return {
        unitCost: UNIT_CONTACT_COMBO,
        billedPhoneCount: billed,
        estimatedCost: round2(billed * UNIT_CONTACT_COMBO),
        runsDisc: true,
        runsReverse: true,
      };
    }
    if (includeReverse) {
      const billed = unconfirmed + goodNeedsReverse;
      return {
        unitCost: UNIT_CALL_COMBO,
        billedPhoneCount: billed,
        estimatedCost: round2(billed * UNIT_CALL_COMBO),
        runsDisc: true,
        runsReverse: true,
      };
    }
    return {
      unitCost: UNIT_DISC,
      billedPhoneCount: unconfirmed,
      estimatedCost: round2(unconfirmed * UNIT_DISC),
      runsDisc: true,
      runsReverse: false,
    };
  }, [counts, includeReverse, billingType]);

  const handleSubmit = async () => {
    if (estimate.billedPhoneCount === 0) {
      onSubmitted();
      return;
    }
    setSubmitting(true);
    setError(null);
    setStage('creating');
    try {
      // Extract phones from lead rows (matching by index)
      const phones = leadRows.map(r => cleanPhone10(r.phone));

      // The HTTPS callable request body limit is 10 MB. With dense leadRows
      // (~194 bytes/row JSON-encoded) that's ~50K rows max per call. We split
      // into 30K-row API batches to leave 40% headroom for outliers and call
      // sequentially under one parentBatchId.
      const API_BATCH_SIZE = 30000;
      const totalBatches = Math.ceil(phones.length / API_BATCH_SIZE);
      const createBatch = httpsCallable(functions, 'createPhoneBatchJob');

      const fullCounts = {
        goodFull: counts.good_full,
        goodNeedsReverse: counts.good_needs_reverse,
        bad: counts.bad,
        unconfirmed: counts.unconfirmed,
      };
      const runType = includeReverse ? 'disconnect_plus_reverse' : 'disconnect';

      let sessionUrl: string | undefined;
      let parentBatchId: string | undefined;

      if (totalBatches <= 1) {
        // ── Single-call path (≤30K rows) ──
        // Keeps the inline-checkout fast path that saves ~5-10s on small
        // uploads. No append/finalize round trip needed.
        const batchResult = await createBatch({
          phones,
          leadRows,
          counts: fullCounts,
          runType,
          cellOnly,
        });
        const data = batchResult.data as { jobId: string; sessionUrl?: string; chunkCount?: number };
        parentBatchId = data.jobId;
        sessionUrl = data.sessionUrl;
        if (data.chunkCount && data.chunkCount > 1) {
          console.log(`[VerifyBatchModal] split into ${data.chunkCount} Firestore chunks (one API call, one checkout)`);
        }
      } else {
        // ── Multi-call append path (large uploads) ──
        // Sequentially upload batches, then finalize once. Progress visible
        // in the modal so the user sees something is happening.
        setStage('uploading');
        setUploadProgress({ current: 1, total: totalBatches });

        for (let i = 0; i < totalBatches; i++) {
          const start = i * API_BATCH_SIZE;
          const end = Math.min(start + API_BATCH_SIZE, phones.length);
          const isFirst = i === 0;

          setUploadProgress({ current: i + 1, total: totalBatches });

          const callPayload = isFirst
            ? {
                phones: phones.slice(start, end),
                leadRows: leadRows.slice(start, end),
                counts: fullCounts,
                runType,
                cellOnly,
                isFinalChunk: false,  // suppress inline checkout
              }
            : {
                phones: phones.slice(start, end),
                leadRows: leadRows.slice(start, end),
                runType,
                cellOnly,
                parentBatchId,        // append mode
              };

          const result = await createBatch(callPayload);
          const data = result.data as { jobId: string; parentBatchId: string };
          if (isFirst) parentBatchId = data.parentBatchId;
        }

        if (!parentBatchId) {
          throw new Error('No parentBatchId returned from first batch');
        }

        // Finalize → one Stripe checkout for the run-level total
        setStage('finalizing');
        const finalizeFn = httpsCallable(functions, 'finalizeVerificationBatch');
        const finResult = await finalizeFn({ parentBatchId });
        const finData = finResult.data as { sessionUrl: string };
        sessionUrl = finData.sessionUrl;
      }

      if (!sessionUrl) {
        // Fallback path: inline / finalize checkout failed. Use the dedicated
        // callable. `parentBatchId` is the parent batch id; checkout.ts reads
        // parentBatchId off the doc and stamps it into Stripe metadata so the
        // webhook flips every sibling chunk to 'paid'.
        setStage('checkout');
        const createCheckout = httpsCallable(functions, 'createCheckoutSession');
        const checkoutResult = await createCheckout({
          tierId: 'verification',
          phoneBatchJobId: parentBatchId,
        });
        sessionUrl = (checkoutResult.data as { sessionUrl: string }).sessionUrl;
      }

      // Redirect to Stripe
      setStage('redirecting');
      window.location.href = sessionUrl!;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to start verification: ${msg}`);
      setSubmitting(false);
      setStage('idle');
      setUploadProgress(null);
    }
  };

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content" style={modalContentStyle}>
        <h2 style={{ margin: '0 0 16px', color: 'var(--gold)' }}>Verify Unconfirmed Phones</h2>

        <div style={{ marginBottom: '20px', color: 'var(--gray-200)', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 12px' }}>
            We found phones in your upload that need verification before we can call them.
            Confirm the breakdown below to proceed with payment.
          </p>
        </div>

        {(() => {
          // Mirror the cost logic: good_needs_reverse only counts toward
          // "needs verification" when reverse will actually run. For per-call
          // clients with reverse opt-out, those phones are already callable
          // and shown as "Already verified (free)".
          const reverseRuns = billingType === 'per_contact' || includeReverse;
          const freeCount = counts.good_full + (reverseRuns ? 0 : counts.good_needs_reverse);
          const needsCount = counts.unconfirmed + (reverseRuns ? counts.good_needs_reverse : 0);
          return (
            <div style={summaryBoxStyle}>
              <SummaryRow label="Already verified (free)" value={freeCount} color="var(--green)" />
              <SummaryRow label="Known bad (removed)" value={counts.bad} color="var(--gray-400)" />
              <SummaryRow label="Needs verification" value={needsCount} color="var(--gold)" />
            </div>
          );
        })()}

        <div style={{ marginBottom: '20px' }}>
          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={includeReverse}
              disabled={reverseToggleLocked || submitting}
              onChange={e => setIncludeReverse(e.target.checked)}
              style={{ accentColor: 'var(--gold)' }}
            />
            <span>
              <strong>Include reverse phone name check</strong>
              {reverseToggleLocked && <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}> &mdash; required for per-contact billing</span>}
            </span>
          </label>
          <label style={{ ...toggleLabelStyle, marginTop: '8px' }}>
            <input
              type="checkbox"
              checked={cellOnly}
              disabled={submitting}
              onChange={e => setCellOnly(e.target.checked)}
              style={{ accentColor: 'var(--gold)' }}
            />
            <span>
              <strong>Cell phones only</strong>
              <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}> &mdash; skip landlines during reverse phone stage</span>
            </span>
          </label>
        </div>

        <div style={costBoxStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span>Phones to verify:</span>
            <strong>{estimate.billedPhoneCount.toLocaleString()}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span>Unit cost:</span>
            <strong>${estimate.unitCost.toFixed(3)} / phone</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(200,168,78,0.3)' }}>
            <span style={{ fontSize: '1.1rem' }}>Total:</span>
            <strong style={{ color: 'var(--gold)', fontSize: '1.25rem' }}>${estimate.estimatedCost.toFixed(2)}</strong>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px', marginBottom: '16px', borderRadius: '6px', background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)' }}>
            {error}
          </div>
        )}

        {submitting && stageLabel && (
          <div style={progressBoxStyle}>
            <span style={spinnerStyle} />
            <span>{stageLabel}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onSkip}
            disabled={submitting}
            className="btn btn-secondary"
            title="Save these phones for later — you can run them from your dashboard at any time"
          >
            Skip unverified records
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || estimate.billedPhoneCount === 0}
            className="btn btn-primary"
          >
            {submitting ? 'Processing...' : `Pay & Verify ($${estimate.estimatedCost.toFixed(2)})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
      <span style={{ color: 'var(--gray-300)' }}>{label}</span>
      <strong style={{ color }}>{value.toLocaleString()}</strong>
    </div>
  );
}

function cleanPhone10(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') return digits.slice(1);
  if (digits.length === 10) return digits;
  return '';
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const modalContentStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
  padding: '32px', maxWidth: '560px', width: '100%',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  maxHeight: '90vh', overflowY: 'auto',
};

const summaryBoxStyle: React.CSSProperties = {
  padding: '16px', marginBottom: '20px', borderRadius: '8px',
  background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)',
};

const toggleLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: '10px',
  cursor: 'pointer', color: 'var(--gray-200)', fontSize: '0.95rem',
  padding: '8px', borderRadius: '6px',
};

const costBoxStyle: React.CSSProperties = {
  padding: '16px', marginBottom: '20px', borderRadius: '8px',
  background: 'rgba(200, 168, 78, 0.05)', border: '1px solid rgba(200, 168, 78, 0.3)',
  color: 'var(--gray-100)',
};

// CSS keyframes for the spinner — injected once via a <style> tag at module load
if (typeof document !== 'undefined' && !document.getElementById('jad-spinner-keyframes')) {
  const style = document.createElement('style');
  style.id = 'jad-spinner-keyframes';
  style.textContent = `@keyframes jad-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

const spinnerStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: '2px solid rgba(200, 168, 78, 0.25)',
  borderTopColor: 'var(--gold)',
  borderRadius: '50%',
  animation: 'jad-spin 0.8s linear infinite',
  verticalAlign: 'middle',
};

const progressBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  padding: '12px 16px',
  borderRadius: '8px',
  background: 'rgba(200, 168, 78, 0.08)',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  color: 'var(--gray-200)',
  fontSize: '0.9rem',
};
